import { css } from "dx-styles";
import { useCallback, useEffect, useState } from "react";

import { ApiError, api } from "../api/client";
import type { AccountSummary, BrowserSession, Device } from "../api/types";
import {
  CopyButton,
  Modal,
  Waypoint,
  button,
  card,
  errorText,
  heading,
  layout,
  mono,
  muted,
  row,
  spread,
  stack,
  subheading,
} from "../components/primitives";
import { expiresIn, relativeTime, shortDate } from "../lib/format";
import { tokens } from "../theme/tokens";

const section = css({ display: "flex", flexDirection: "column", gap: tokens.space.md });

const listRow = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: tokens.space.md,
  padding: `${tokens.space.sm} 0`,
  borderTop: `1px solid ${tokens.color.rule}`,
  "&:first-of-type": { borderTop: "none" },
});

/// The row's action must not wrap or shrink: at phone width "Sign out" was
/// breaking across two lines while the meta text kept the space.
const action = css({ flex: "none", whiteSpace: "nowrap" });

const badge = css({
  border: `1px solid ${tokens.color.rule}`,
  borderRadius: "999px",
  padding: `1px ${tokens.space.sm}`,
  fontSize: "11px",
  fontWeight: 600,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: tokens.color.pencil,
});

const danger = css({
  border: `1px solid ${tokens.color.signal}`,
  borderRadius: tokens.radius.md,
  padding: tokens.space.lg,
  backgroundColor: tokens.color.paper,
  display: "flex",
  flexDirection: "column",
  gap: tokens.space.md,
});

const codeBlock = css({
  fontSize: "18px",
  letterSpacing: "0.12em",
  padding: tokens.space.sm,
  border: `1px solid ${tokens.color.rule}`,
  borderRadius: tokens.radius.sm,
  backgroundColor: tokens.color.kraft,
  overflowWrap: "anywhere",
});

const field = css({
  minHeight: "36px",
  padding: `0 ${tokens.space.sm}`,
  borderRadius: tokens.radius.sm,
  border: `1px solid ${tokens.color.rule}`,
  backgroundColor: tokens.color.paper,
});

/**
 * Stands in for a code nobody can read back: the shape of a real one — six
 * groups of four, as `RecoveryCode.generate` emits — so the box does not resize
 * when a fresh code appears in it.
 */
const MASK = "••••-••••-••••-••••-••••-••••";

interface Props {
  readonly onSignedOut: () => void;
}

export function Account({ onSignedOut }: Props) {
  const [summary, setSummary] = useState<AccountSummary | null>(null);
  const [devices, setDevices] = useState<readonly Device[]>([]);
  const [sessions, setSessions] = useState<readonly BrowserSession[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [freshCode, setFreshCode] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [typed, setTyped] = useState("");

  /// A 401 anywhere on this screen means the session this page is standing on is
  /// gone — which is the expected outcome of revoking your own session, not an
  /// error to render.
  const guard = useCallback(
    (caught: unknown, fallback: string) => {
      if (caught instanceof ApiError && caught.status === 401) {
        onSignedOut();
        return;
      }
      setError(caught instanceof Error ? caught.message : fallback);
    },
    [onSignedOut],
  );

  const reload = useCallback(async () => {
    try {
      const [account, deviceList, sessionList] = await Promise.all([
        api.account(),
        api.devices(),
        api.sessions(),
      ]);
      setSummary(account);
      setDevices(deviceList);
      setSessions(sessionList);
      setError(null);
    } catch (caught) {
      guard(caught, "Could not load this account.");
    }
  }, [guard]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const revokeDevice = useCallback(
    async (device: Device) => {
      setBusy(device.id);
      try {
        await api.revokeDevice(device.id);
        await reload();
      } catch (caught) {
        guard(caught, "Could not revoke that device.");
      } finally {
        setBusy(null);
      }
    },
    [guard, reload],
  );

  const revokeSession = useCallback(
    async (session: BrowserSession) => {
      setBusy(session.id);
      try {
        await api.revokeSession(session.id);
        // Revoking this browser's own session ends the page's session too. The
        // server already cleared the cookies; leaving would otherwise wait for
        // the next request to notice.
        if (session.isCurrent) {
          onSignedOut();
          return;
        }
        await reload();
      } catch (caught) {
        guard(caught, "Could not revoke that session.");
      } finally {
        setBusy(null);
      }
    },
    [guard, onSignedOut, reload],
  );

  const rotate = useCallback(async () => {
    setRotating(true);
    try {
      const issued = await api.rotateRecoveryCode();
      setFreshCode(issued.recoveryCode);
      await reload();
    } catch (caught) {
      guard(caught, "Could not issue a new recovery code.");
    } finally {
      setRotating(false);
    }
  }, [guard, reload]);

  const confirmDelete = useCallback(async () => {
    try {
      await api.deleteAccount(typed);
      setDeleting(false);
      onSignedOut();
    } catch (caught) {
      guard(caught, "Could not delete this account.");
    }
  }, [guard, onSignedOut, typed]);

  if (!summary) {
    return (
      <main className={layout}>
        <h1 className={heading}>Settings</h1>
        {error ? <p className={errorText}>{error}</p> : <p className={muted}>Loading…</p>}
      </main>
    );
  }

  return (
    <main className={layout}>
      <h1 className={heading}>Settings</h1>
      {error ? <p className={errorText}>{error}</p> : null}

      <section className={section}>
        <Waypoint label="Devices" count={devices.length} />
        <div className={card}>
          {devices.length === 0 ? (
            <p className={muted}>
              No authorized device. Signing in again from the app registers one.
            </p>
          ) : (
            devices.map((device) => (
              <div key={device.id} className={listRow}>
                <div>
                  <div className={subheading}>{device.model}</div>
                  <div className={muted}>
                    {device.platform} · added {shortDate(device.createdAt)} · last seen{" "}
                    {relativeTime(device.lastSeenAt)}
                  </div>
                </div>
                <button
                  type="button"
                  className={`${button({ tone: "danger", size: "sm" })} ${action}`}
                  disabled={busy === device.id}
                  onClick={() => void revokeDevice(device)}
                >
                  Revoke
                </button>
              </div>
            ))
          )}
        </div>
        <p className={muted}>
          Revoking a device signs the app out of this account on that phone. It keeps the
          templates it already downloaded, as local copies.
        </p>
      </section>

      <section className={section}>
        <Waypoint label="Browser sessions" count={sessions.length} />
        <div className={card}>
          {sessions.map((session) => (
            <div key={session.id} className={listRow}>
              <div>
                <div className={row}>
                  <span className={subheading}>{session.browser}</span>
                  {session.isCurrent ? <span className={badge}>current</span> : null}
                </div>
                <div className={muted}>
                  Authorized {shortDate(session.createdAt)} ·{" "}
                  {session.isCurrent
                    ? expiresIn(session.expiresAt)
                    : `last seen ${relativeTime(session.lastSeenAt)}`}
                </div>
              </div>
              <button
                type="button"
                className={`${button({ tone: "danger", size: "sm" })} ${action}`}
                disabled={busy === session.id}
                onClick={() => void revokeSession(session)}
              >
                {session.isCurrent ? "Sign out" : "Revoke"}
              </button>
            </div>
          ))}
        </div>
      </section>

      <section className={section}>
        <Waypoint label="Recovery code" />
        <div className={card}>
          <div className={stack}>
            <p className={muted}>
              The only way back in if you lose every authorized device. Shown once — store it
              somewhere that is not the phone.
            </p>
            {freshCode ? (
              <>
                <div className={`${codeBlock} ${mono}`}>{freshCode}</div>
                <div className={row}>
                  <CopyButton value={freshCode} label="Copy code" />
                  <button
                    type="button"
                    className={button({ tone: "primary", size: "sm" })}
                    onClick={() => setFreshCode(null)}
                  >
                    I saved it
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className={`${codeBlock} ${mono}`} aria-label="Recovery code, hidden">
                  {MASK}
                </div>
                <div className={spread}>
                  <span className={muted}>
                    {summary.hasRecoveryCode
                      ? `Issued ${shortDate(summary.recoveryCodeIssuedAt)}`
                      : "No code on this account"}
                  </span>
                  <button
                    type="button"
                    className={button({ size: "sm" })}
                    disabled={rotating}
                    onClick={() => void rotate()}
                  >
                    {rotating ? "Generating…" : "Generate a new code"}
                  </button>
                </div>
                <p className={muted}>
                  Generating one invalidates the old code. It does not sign any device out.
                </p>
              </>
            )}
          </div>
        </div>
      </section>

      <section className={section}>
        <Waypoint label="Danger zone" />
        <div className={danger}>
          <div className={subheading}>Delete account</div>
          <p className={muted}>
            Removes {summary.collectionCount} {summary.collectionCount === 1 ? "collection" : "collections"},{" "}
            {summary.itemCount} {summary.itemCount === 1 ? "item" : "items"}, {summary.deviceCount}{" "}
            {summary.deviceCount === 1 ? "device" : "devices"} and {summary.sessionCount}{" "}
            {summary.sessionCount === 1 ? "session" : "sessions"}. Published links start answering{" "}
            <span className={mono}>410 Gone</span>, and subscribers see that the author deleted the
            source. Templates already on their phones stay, as local copies.
          </p>
          <div>
            <button
              type="button"
              className={button({ tone: "danger" })}
              onClick={() => {
                setTyped("");
                setDeleting(true);
              }}
            >
              Delete everything…
            </button>
          </div>
        </div>
      </section>

      <Modal
        open={deleting}
        onOpenChange={(open) => setDeleting(open)}
        title="Delete this account?"
        description="This cannot be undone. Nothing is archived and nothing is recoverable."
      >
        <div className={stack}>
          <ul className={muted}>
            <li>
              {summary.publishedCount} published{" "}
              {summary.publishedCount === 1 ? "collection stops" : "collections stop"} being served
              and its links answer 410 from then on.
            </li>
            <li>Every item, device, browser session and revision is deleted.</li>
            <li>Your recovery code stops working, so there is no way back in.</li>
          </ul>
          <label className={stack}>
            <span>
              Type <span className={mono}>delete</span> to confirm
            </span>
            <input
              className={field}
              value={typed}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              onChange={(event) => setTyped(event.target.value)}
            />
          </label>
          <div className={row}>
            <button
              type="button"
              className={button({ tone: "danger" })}
              disabled={typed.trim().toLowerCase() !== "delete"}
              onClick={() => void confirmDelete()}
            >
              Delete account
            </button>
            <button type="button" className={button()} onClick={() => setDeleting(false)}>
              Keep it
            </button>
          </div>
        </div>
      </Modal>
    </main>
  );
}
